import React from "react";
import AppLayout from "@/components/layouts/AppLayout";
import { Head, useForm } from "@inertiajs/react";
import { preference_path } from "@/routes";
import type Preference from "@/types/serializers/Preference";
import type { ReactNode } from "react";
import { formatHoursLabel } from "./utils";
import { Box, Heading, VStack, Input, Button, Text, Grid, GridItem, HStack } from "@chakra-ui/react";
import { Field } from "@/components/ui/field";
import { Tooltip } from "@/components/ui/tooltip";
import { useAnalytics } from "@/hooks/useAnalytics";
import { LuInfo } from "react-icons/lu";

interface PreferencesProps {
  preference: Preference;
}

interface PreferencesFormData {
  focus_minutes_per_day: number;
  day_start_time: string;
  day_end_time: string;
  lunch_start_time: string;
  lunch_end_time: string;
  [key: string]: string | number;
}

const Show = ({ preference }: PreferencesProps) => {
  const { data, setData, put, errors, transform, processing, reset } = useForm<PreferencesFormData>({
    focus_minutes_per_day: preference?.focus_minutes_per_day ?? 240,
    day_start_time: preference?.day_start_time_hhmm ?? "09:00",
    day_end_time: preference?.day_end_time_hhmm ?? "17:00",
    lunch_start_time: preference?.lunch_start_time_hhmm ?? "13:00",
    lunch_end_time: preference?.lunch_end_time_hhmm ?? "14:00",
  });

  const TRACK = useAnalytics();

  transform((payload) => ({ preference: payload }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    put(preference_path(), {
      onSuccess: () => {
        TRACK.PREFERENCES_UPDATED(data);
      },
      onError: () => reset(),
    });
  };

  const errorList = Object.entries(errors).map(([_field, msgs]) => `${msgs}`);

  return (
    <Box>
      <Head title="Preferences - Muro" />
      <Heading as="h1" size="xl" mb={6} color="primary-bold">
        Preferences
      </Heading>

      {errorList.length > 0 && (
        <Box bg="red.50" borderWidth="1px" borderColor="red.200" borderRadius="md" p={4} mb={4}>
          <VStack as="ul" gap={1} alignItems="flex-start">
            {errorList.map((msg, idx) => (
              <Text as="li" key={idx} fontSize="sm" color="red.700">
                {msg}
              </Text>
            ))}
          </VStack>
        </Box>
      )}

      <form onSubmit={handleSubmit}>
        <VStack gap={6} maxW="md" alignItems="stretch">
          <Box>
            <HStack gap={1.5} mb={1} alignItems="center">
              <Box
                as="label"
                // @ts-expect-error - htmlFor is a valid HTML attribute for label elements
                htmlFor="focus_minutes_per_day"
                fontSize="sm"
                fontWeight="medium"
                color="gray.700"
              >
                Focus minutes per day
              </Box>
              <Tooltip
                content="Studies suggest that 4 to 6 hours of focused work per day is ideal for maintaining productivity and avoiding burnout."
                showArrow
              >
                <Box as="span" color="gray.500" cursor="help" display="inline-flex">
                  <LuInfo size={16} />
                </Box>
              </Tooltip>
            </HStack>
            <Input
              id="focus_minutes_per_day"
              name="focus_minutes_per_day"
              type="number"
              min={45}
              max={480}
              value={data.focus_minutes_per_day}
              onChange={(e) => setData((prev) => ({ ...prev, focus_minutes_per_day: Number(e.target.value) }))}
            />
            <Text mt={1} fontSize="sm" color="gray.500">
              ≈ {formatHoursLabel(data.focus_minutes_per_day)}
            </Text>
          </Box>

          <Grid templateColumns="repeat(2, 1fr)" gap={4}>
            <GridItem>
              <Field label="Day start time" htmlFor="day_start_time">
                <Input
                  id="day_start_time"
                  name="day_start_time"
                  type="time"
                  value={data.day_start_time}
                  onChange={(e) => setData((prev) => ({ ...prev, day_start_time: e.target.value }))}
                />
              </Field>
            </GridItem>
            <GridItem>
              <Field label="Day end time" htmlFor="day_end_time">
                <Input
                  id="day_end_time"
                  name="day_end_time"
                  type="time"
                  value={data.day_end_time}
                  onChange={(e) => setData((prev) => ({ ...prev, day_end_time: e.target.value }))}
                />
              </Field>
            </GridItem>
          </Grid>

          <Box>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <GridItem>
                <Field label="Lunch start time" htmlFor="lunch_start_time">
                  <Input
                    id="lunch_start_time"
                    name="lunch_start_time"
                    type="time"
                    value={data.lunch_start_time}
                    onChange={(e) => setData((prev) => ({ ...prev, lunch_start_time: e.target.value }))}
                  />
                </Field>
              </GridItem>
              <GridItem>
                <Field label="Lunch end time" htmlFor="lunch_end_time">
                  <Input
                    id="lunch_end_time"
                    name="lunch_end_time"
                    type="time"
                    value={data.lunch_end_time}
                    onChange={(e) => setData((prev) => ({ ...prev, lunch_end_time: e.target.value }))}
                  />
                </Field>
              </GridItem>
            </Grid>
            <Text mt={1} fontSize="sm" color="gray.500">
              No focus events will be scheduled during lunch.
            </Text>
          </Box>

          <Box>
            <Button
              type="submit"
              disabled={processing}
              bg="primary-base"
              color="on-primary-base"
              _hover={{ bg: "primary-hover" }}
              cursor="pointer"
            >
              {processing ? "Saving..." : "Save"}
            </Button>
          </Box>
        </VStack>
      </form>
    </Box>
  );
};

Show.layout = (page: ReactNode) => <AppLayout>{page}</AppLayout>;

export default Show;
